import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';

import type {
  TranscodeJob,
  TrackedJob,
  TranscodeJobFilters,
  TranscodeJobsMeta,
  StallDetectionConfig,
} from '@shared/types';

import {
  DEFAULT_STALL_CONFIG,
  detectStallStatus,
  createTrackedJob,
} from '@shared/types';

// Re-export types for convenience
export type { TranscodeJob, TrackedJob, TranscodeJobFilters, TranscodeJobsMeta };

interface SuccessPaginationState {
  jobs: TranscodeJob[];
  page: number;
  totalCount: number;
  totalPages: number;
  loading: boolean;
}

interface TranscodeJobsState {
  // Primary collections
  processing: Map<string, TrackedJob>;
  assetToJobID: Map<string, string>; // assetID → jobID for 'complete' message correlation
  failed: TranscodeJob[];
  success: TranscodeJob[]; // From WSS (live completions)
  successPaginated: SuccessPaginationState; // From API (historical, paginated)

  // Stall detection config
  stallConfig: StallDetectionConfig;

  // Metadata
  lastFetched: number | null;
  lastMeta: TranscodeJobsMeta | null;
  loading: boolean;
  error: string | null;

  // Actions
  fetchJobs: (accessToken: string, logout: () => void, filters?: TranscodeJobFilters) => Promise<void>;
  fetchSuccessJobs: (accessToken: string, logout: () => void, page?: number, filters?: Omit<TranscodeJobFilters, 'status' | 'page'>) => Promise<void>;
  updateJobFromWSS: (
    jobID: string,
    assetID: string | undefined,
    eventID: string | undefined,
    clientID: string,
    progress: number,
    status: string,
    raw: unknown
  ) => void;
  reEvaluateStallStatus: () => void;
  setStallConfig: (config: Partial<StallDetectionConfig>) => void;
  clearJobs: () => void;

  // Legacy compatibility
  updateJobProgress: (jobId: string, progress: number, status?: string) => void;
}

export const useTranscodeJobsStore = create<TranscodeJobsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      processing: new Map(),
      assetToJobID: new Map(),
      failed: [],
      success: [],
      successPaginated: {
        jobs: [],
        page: 1,
        totalCount: 0,
        totalPages: 0,
        loading: false,
      },
      stallConfig: DEFAULT_STALL_CONFIG,
      lastFetched: null,
      lastMeta: null,
      loading: false,
      error: null,

      fetchJobs: async (accessToken: string, logout: () => void, filters?: TranscodeJobFilters) => {
        set({ loading: true, error: null });
        try {
          const params = new URLSearchParams();
          if (filters?.clientID) params.set('clientID', filters.clientID);
          if (filters?.status) params.set('status', filters.status);
          if (filters?.startDate) params.set('startDate', filters.startDate);
          if (filters?.endDate) params.set('endDate', filters.endDate);
          if (filters?.limit) params.set('limit', filters.limit.toString());

          const url = `${API_BASE_URL}/api/transcode-jobs${params.toString() ? `?${params}` : ''}`;
          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            if (handleApiError(response, logout)) return;
            throw new Error(`Failed to fetch transcode jobs: ${response.statusText}`);
          }

          const data = await response.json();
          const jobs: TranscodeJob[] = data.jobs;
          const meta: TranscodeJobsMeta = data.meta;
          const now = Date.now();

          // Categorize jobs by status
          // Note: API only returns processing and failed jobs by default
          // Success jobs come only from WSS (processing -> success transitions)
          const newProcessing = new Map<string, TrackedJob>();
          const newAssetToJobID = new Map<string, string>();
          const newFailed: TranscodeJob[] = [];

          for (const job of jobs) {
            if (job.status === 'processing') {
              const tracked = createTrackedJob(job, now);
              newProcessing.set(job.id, tracked);
              if (job.assetID) {
                newAssetToJobID.set(job.assetID, job.id);
              }
            } else if (job.status === 'failed') {
              newFailed.push(job);
            }
          }

          set({
            processing: newProcessing,
            assetToJobID: newAssetToJobID,
            failed: newFailed,
            // Keep existing success jobs (from WSS) - don't overwrite
            loading: false,
            lastFetched: now,
            lastMeta: meta,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          set({ error: errorMessage, loading: false });
        }
      },

      /**
       * Fetch paginated success jobs from the API
       * This fetches historical success jobs, separate from WSS live completions
       */
      fetchSuccessJobs: async (accessToken: string, logout: () => void, page = 1, filters?: Omit<TranscodeJobFilters, 'status' | 'page'>) => {
        set((state) => ({
          successPaginated: { ...state.successPaginated, loading: true },
        }));

        try {
          const params = new URLSearchParams();
          params.set('status', 'success');
          params.set('page', page.toString());
          if (filters?.clientID) params.set('clientID', filters.clientID);
          if (filters?.startDate) params.set('startDate', filters.startDate);
          if (filters?.endDate) params.set('endDate', filters.endDate);
          if (filters?.limit) params.set('limit', filters.limit.toString());

          const url = `${API_BASE_URL}/api/transcode-jobs?${params}`;
          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            if (handleApiError(response, logout)) return;
            throw new Error(`Failed to fetch success jobs: ${response.statusText}`);
          }

          const data = await response.json();
          const jobs: TranscodeJob[] = data.jobs;
          const meta: TranscodeJobsMeta = data.meta;

          set({
            successPaginated: {
              jobs,
              page: meta.page ?? 1,
              totalCount: meta.totalCount ?? jobs.length,
              totalPages: meta.totalPages ?? 1,
              loading: false,
            },
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          set((state) => ({
            error: errorMessage,
            successPaginated: { ...state.successPaginated, loading: false },
          }));
        }
      },

      /**
       * Update job from WebSocket message
       * Handles start, processing, and complete message types
       */
      updateJobFromWSS: (jobID, assetID, eventID, clientID, progress, status, raw) => {
        set((state) => {
          const now = Date.now();
          const newProcessing = new Map(state.processing);
          const newAssetToJobID = new Map(state.assetToJobID);
          let newFailed = state.failed;
          let newSuccess = state.success;

          // Handle completion (status === 'success')
          if (status === 'success') {
            // Find job by assetID correlation (complete messages don't have jobID)
            const correlatedJobID = assetID ? newAssetToJobID.get(assetID) : undefined;
            const actualJobID = correlatedJobID || jobID;
            const existingJob = newProcessing.get(actualJobID);

            if (existingJob) {
              // Move from processing to success
              newProcessing.delete(actualJobID);
              if (assetID) newAssetToJobID.delete(assetID);

              const completedJob: TranscodeJob = {
                ...existingJob,
                progress: 100,
                status: 'success',
                updatedAt: new Date().toISOString(),
              };
              newSuccess = [completedJob, ...newSuccess];
            }

            return {
              processing: newProcessing,
              assetToJobID: newAssetToJobID,
              success: newSuccess,
            };
          }

          // Handle failure
          if (status === 'failed') {
            const existingJob = newProcessing.get(jobID);

            if (existingJob) {
              newProcessing.delete(jobID);
              if (assetID) newAssetToJobID.delete(assetID);

              const failedJob: TranscodeJob = {
                ...existingJob,
                progress,
                status: 'failed',
                updatedAt: new Date().toISOString(),
              };
              newFailed = [failedJob, ...newFailed];
            }

            return {
              processing: newProcessing,
              assetToJobID: newAssetToJobID,
              failed: newFailed,
            };
          }

          // Handle processing/start (status === 'processing' or 'start')
          const existingJob = newProcessing.get(jobID);

          if (existingJob) {
            // Update existing job
            const progressIncreased = progress > existingJob.lastProgressValue;
            const updatedJob: TrackedJob = {
              ...existingJob,
              progress,
              status: 'processing',
              lastProgressValue: progress,
              lastProgressChangeAt: progressIncreased ? now : existingJob.lastProgressChangeAt,
              stallStatus: 'normal', // Will be re-evaluated
            };
            updatedJob.stallStatus = detectStallStatus(updatedJob, now, state.stallConfig);
            newProcessing.set(jobID, updatedJob);
          } else {
            // New job from WSS - create minimal tracked job
            const newJob: TrackedJob = {
              id: jobID,
              status: 'processing',
              progress,
              duration: null,
              filesize: null,
              message: null,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              assetID: assetID ?? null,
              clientID,
              eventID: eventID ?? null,
              jobType: 'transcode',
              destBucket: null,
              destKey: null,
              srcBucket: null,
              srcKey: null,
              thumbnails: null,
              playlist: null,
              lastProgressValue: progress,
              lastProgressChangeAt: now,
              stallStatus: 'normal',
            };
            newProcessing.set(jobID, newJob);
            if (assetID) {
              newAssetToJobID.set(assetID, jobID);
            }
          }

          return {
            processing: newProcessing,
            assetToJobID: newAssetToJobID,
          };
        });
      },

      /**
       * Re-evaluate stall status for all processing jobs
       * Should be called on an interval (e.g., every 30 seconds)
       */
      reEvaluateStallStatus: () => {
        set((state) => {
          const now = Date.now();
          const newProcessing = new Map<string, TrackedJob>();
          let hasChanges = false;

          for (const [jobID, job] of state.processing) {
            const newStallStatus = detectStallStatus(job, now, state.stallConfig);

            if (newStallStatus !== job.stallStatus) {
              hasChanges = true;
              newProcessing.set(jobID, { ...job, stallStatus: newStallStatus });
            } else {
              newProcessing.set(jobID, job);
            }
          }

          return hasChanges ? { processing: newProcessing } : {};
        });
      },

      setStallConfig: (config) => {
        set((state) => ({
          stallConfig: { ...state.stallConfig, ...config },
        }));
      },

      clearJobs: () => {
        set({
          processing: new Map(),
          assetToJobID: new Map(),
          failed: [],
          success: [],
          successPaginated: {
            jobs: [],
            page: 1,
            totalCount: 0,
            totalPages: 0,
            loading: false,
          },
          lastFetched: null,
          lastMeta: null,
        });
      },

      // Legacy: updateJobProgress for backward compatibility with existing WSS handler
      updateJobProgress: (jobId, progress, status) => {
        get().updateJobFromWSS(jobId, undefined, undefined, '', progress, status || 'processing', null);
      },
    }),
    { name: 'transcode-jobs-store' }
  )
);

/**
 * Selector to get all jobs combined (processing + failed + success)
 * Use this if you need the legacy flat array of all jobs
 */
export function useAllTranscodeJobs(): TranscodeJob[] {
  const processing = useTranscodeJobsStore((state) => state.processing);
  const failed = useTranscodeJobsStore((state) => state.failed);
  const success = useTranscodeJobsStore((state) => state.success);

  return [
    ...Array.from(processing.values()),
    ...failed,
    ...success,
  ];
}
